import { createSession } from '../api/session';
import { ENV, getSessionCacheKey } from '../config/environment';
import { bootstrapLogger } from '../utils/logger';

// Interface for session response
interface CreateSessionResponse {
  session_id: string;
  token: string;
  fbcreds: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
  };
}

async function initializeFirebaseAndCreateWidget(workspaceId: string) {
  try {

    // Try to load cached session first
    const cached = loadCachedSession(workspaceId);
    let sessionData: CreateSessionResponse;

    if (cached) {
      bootstrapLogger.info('Using cached session');
      sessionData = cached;
    } else {
      bootstrapLogger.info('Creating new session');
      // Create new session using centralized API
      sessionData = await createSession({workspace_id: workspaceId});
      cacheSession(sessionData, workspaceId);
    }

    // Basic validation that we got valid credentials
    if (!sessionData.fbcreds || !sessionData.token) {
      throw new Error('Invalid session data received');
    }

    // Session initialized successfully, create the widget UI
    createWidgetUI();

  } catch (error) {
    bootstrapLogger.error('Failed to initialize BotForge', error);

    // Show a minimal error indicator instead of the full widget
    showInitializationError();
  }
}

function loadCachedSession(workspaceId: string): CreateSessionResponse | null {
  try {
    const cacheKey = getSessionCacheKey(workspaceId);
    const raw = localStorage.getItem(cacheKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function cacheSession(data: CreateSessionResponse, workspaceId: string): void {
  const cacheKey = getSessionCacheKey(workspaceId);
  localStorage.setItem(cacheKey, JSON.stringify(data));
}

function createWidgetUI() {
  bootstrapLogger.info('Creating widget UI');
  
  const launcherIframe = document.createElement('iframe');
  launcherIframe.src = `${ENV.widgetBaseUrl}/launcher.html`;
  Object.assign(launcherIframe.style, {
    position: 'fixed',
    bottom: '0px',
    right: '0px',
    width: '120px',
    height: '80px',
    border: 'none',
    background: "transparent",
    zIndex: '99999',
    overflow: 'hidden',
    animation: 'fadeIn 0.3s ease-in-out',
  });

  document.body.appendChild(launcherIframe);

  //now let's append the widget iframe
  const widgetIframe = document.createElement('iframe');

  // Pass settings via URL parameters
  const params = new URLSearchParams({
    workspaceId: window.BotForgeSettings?.workspaceId || ''
  });
  widgetIframe.src = `${ENV.widgetBaseUrl}/widget.html?${params}`;

  Object.assign(widgetIframe.style, {
    position: 'fixed',
    bottom: '0px',
    right: '0px',
    width: '100%',
    height: '100%',
    border: 'none',
    background: 'transparent',
    display: 'none', // initially hidden
    zIndex: '99998',
    transform: 'scale(0.1)',
    opacity: '0',
    transition: 'all 0.2s ease-in-out',
    transformOrigin: 'bottom right', // important to grow from launcher position
  });

  document.body.appendChild(widgetIframe);

  // Listen for messages to toggle the widget visibility and relay unread count updates
  window.addEventListener('message', (event) => {
    if (event.data.type === 'widget-log') {
      const { level, message, data } = event.data;
      const style = 'color: #3b82f6; font-weight: bold;';
      
      if (data !== undefined) {
        console[level as 'log' | 'info' | 'warn' | 'error' | 'debug'](`%c[Widget]`, style, message, data);
      } else {
        console[level as 'log' | 'info' | 'warn' | 'error' | 'debug'](`%c[Widget]`, style, message);
      }
      return;
    }

    if (event.data.type === 'toggle-widget') {
      const isHidden = widgetIframe.style.display === 'none';

      if (isHidden) {
        widgetIframe.style.display = 'block';

        // Trigger reflow to ensure transition runs
        void widgetIframe.offsetWidth;

        widgetIframe.contentWindow?.postMessage({type: 'open-widget'}, "*");

        widgetIframe.style.opacity = '1';
        widgetIframe.style.transform = 'scale(1)';
        widgetIframe.style.right = '0px'; // Reset to full position
      } else {
        widgetIframe.style.opacity = '0';
        widgetIframe.style.transform = 'scale(0.1)';
        widgetIframe.style.right = '20px'; // Move to the launcher position

        widgetIframe.contentWindow?.postMessage({type: 'close-widget'}, "*");

        // Wait for the transition to complete before hiding
        setTimeout(() => {
          widgetIframe.style.display = 'none';
        }, 300);
      }
    } else if (event.data.type === 'unread-count-update') {
      bootstrapLogger.debug('Received unread count update', event.data.count);
      // Relay unread count updates from widget to launcher, including optional preview_conversation_id
      launcherIframe.contentWindow?.postMessage({
        type: 'unread-count-update',
        count: event.data.count,
        preview: event.data.preview,
        preview_conversation_id: event.data.preview_conversation_id,
        header_color: event.data.header_color,
        chat_name: event.data.chat_name,
        icon_url: event.data.icon_url,
      }, "*");
    } else if (event.data.type === 'widget-config-update') {
      bootstrapLogger.debug('Received widget config update', event.data);
      // Relay widget config updates from widget to launcher
      launcherIframe.contentWindow?.postMessage({
        type: 'widget-config-update',
        headerColor: event.data.headerColor,
        iconUrl: event.data.iconUrl,
      }, "*");
    }
  });
}

function showInitializationError() {
  bootstrapLogger.error('Showing initialization error UI');
  
  // Create a minimal error indicator instead of the full widget
  const errorElement = document.createElement('div');
  errorElement.innerHTML = `
    <div style="
      position: fixed;
      bottom: 20px;
      right: 20px;
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background-color: #dc3545;
      display: flex;
      justify-content: center;
      align-items: center;
      box-shadow: 0 4px 20px rgba(220, 53, 69, 0.3);
      z-index: 99999;
      cursor: pointer;
      opacity: 0.7;
    " title="Widget initialization failed">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>
    </div>
  `;

  errorElement.onclick = () => {
    bootstrapLogger.info('User clicked error indicator');
    alert('Widget failed to initialize. Please refresh the page or contact support.');
  };

  document.body.appendChild(errorElement);
}

// Avoid injecting multiple times
if (!window.BotForge) {
  window.BotForge = {
    init: (options: {workspaceId: string}) => {
      window.BotForgeSettings = {
        workspaceId: options.workspaceId,
      };
      initializeFirebaseAndCreateWidget(options.workspaceId);
    }
  };
}

